<!-- views/admin/donations.vue — Back-office donations avec Tailwind CSS -->
<template>
  <main class="h-screen bg-[#F5EFE0] font-sans p-4 md:p-8 overflow-y-auto">

    <!-- ── En-tête ───────────────────────────────────────── -->
    <div class="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
      <div>
        <h1 class="font-serif text-2xl md:text-3xl font-bold text-[#1A1A18]">Gestion des Dons</h1>
        <p class="text-sm text-[#1A1A18]/60 mt-1">Tableau de bord complet : dons, paramètres, devises et montants</p>
      </div>
      <div 
        class="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium"
        :class="donationsEnabled ? 'bg-[#7A9E87]/10 border-[#7A9E87]/30 text-[#1B3A2D]' : 'bg-[#C85A2A]/10 border-[#C85A2A]/20 text-[#C85A2A]'"
      >
        <span class="w-2 h-2 rounded-full bg-current"></span>
        <span>Dons {{ donationsEnabled ? 'activés' : 'désactivés' }}</span>
      </div>
    </div>

    <!-- ── Tabs ───────────────────────────────────────────── -->
    <div class="flex gap-1 border-b border-[#1A1A18]/12 mb-8 overflow-x-auto">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all whitespace-nowrap border-b-2"
        :class="activeTab === tab.id 
          ? 'text-[#C85A2A] border-[#C85A2A]' 
          : 'text-[#1A1A18]/60 border-transparent hover:text-[#C85A2A]'"
        @click="activeTab = tab.id"
      >
        <component :is="tab.icon" class="w-4 h-4" />
        {{ tab.label }}
        <span v-if="tab.badge" class="bg-[#C85A2A] text-white rounded-full text-xs px-2 py-0.5 ml-1">{{ tab.badge }}</span>
      </button>
    </div>

    <!-- ════════════════════════════════════════════════════
         TAB 1 — LISTE DES DONS
    ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'donations'" class="flex flex-col gap-6">

      <!-- Stats KPIs -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div v-for="kpi in donationKpis" :key="kpi.label" class="bg-white rounded-lg p-5 shadow-sm">
          <div class="font-serif text-2xl font-bold text-[#C85A2A]">{{ kpi.value }}</div>
          <div class="text-xs text-[#1A1A18]/60 mt-1">{{ kpi.label }}</div>
        </div>
      </div>

      <!-- Filtres -->
      <div class="flex flex-col sm:flex-row flex-wrap gap-3 items-center bg-white rounded-lg p-5 shadow-sm">
        <div class="relative flex-1 min-w-[200px]">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A18]/60" />
          <input 
            v-model="filters.search" 
            @input="debounceSearch" 
            placeholder="Nom, email, référence…" 
            class="w-full pl-9 pr-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]"
          />
        </div>
        <select v-model="filters.status" @change="loadDonations" class="px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm bg-white focus:outline-none focus:border-[#C85A2A]">
          <option value="">Tous statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmé</option>
          <option value="failed">Échoué</option>
          <option value="refunded">Remboursé</option>
        </select>
        <select v-model="filters.currency_code" @change="loadDonations" class="px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm bg-white focus:outline-none focus:border-[#C85A2A]">
          <option value="">Toutes devises</option>
          <option v-for="c in currencies" :key="c.code" :value="c.code">{{ c.code }}</option>
        </select>
        <select v-model="filters.payment_method" @change="loadDonations" class="px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm bg-white focus:outline-none focus:border-[#C85A2A]">
          <option value="">Tous paiements</option>
          <option value="card">Carte</option>
          <option value="mobile_money">Mobile Money</option>
        </select>
        <button class="inline-flex items-center gap-2 px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A] transition" @click="exportCsv">
          <Download class="w-4 h-4" />Exporter
        </button>
      </div>

      <!-- Table -->
      <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="w-9 h-9 rounded-full border-3 border-[#1A1A18]/12 border-t-[#C85A2A] animate-spin"></div>
        </div>
        <table v-else class="w-full text-sm">
          <thead class="bg-[#F5EFE0]">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Référence</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Donateur</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Montant</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">EUR</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Projet</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Méthode</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Statut</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Date</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!donations.length">
              <td colspan="9" class="text-center text-[#1A1A18]/60 py-8">Aucun don trouvé</td>
            </tr>
            <tr v-for="don in donations" :key="don.id" class="border-b border-[#EDE4CC] hover:bg-[#F5EFE0] transition">
              <td class="px-4 py-3 text-[#1A1A18]/60 text-xs font-medium">{{ don.invoice_number || '#' + don.id }}</td>
              <td class="px-4 py-3">
                <div class="font-medium text-[#1A1A18]">{{ don.is_anonymous ? 'Anonyme' : (don.donor_name || '—') }}</div>
                <div v-if="!don.is_anonymous && don.donor_email" class="text-xs text-[#1A1A18]/60">{{ don.donor_email }}</div>
              </td>
              <td class="px-4 py-3 font-semibold text-[#1A1A18]">{{ don.amount }} {{ don.currency_code }}</td>
              <td class="px-4 py-3 text-sm text-[#7A9E87]">{{ fmtEur(don.amount_eur) }}</td>
              <td class="px-4 py-3">{{ don.project?.title || don.project_slug || '—' }}</td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center px-2 py-1 rounded-full bg-[#EDE4CC] text-[#1A1A18]/60 text-xs">
                  {{ don.payment_method === 'card' ? '💳 Carte' : '📱 Mobile' }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase"
                  :class="{
                    'bg-amber-100 text-amber-700': don.status === 'pending',
                    'bg-green-100 text-green-700': don.status === 'confirmed',
                    'bg-red-100 text-red-700': don.status === 'failed',
                    'bg-gray-100 text-gray-600': don.status === 'refunded'
                  }">
                  {{ statusLabel(don.status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-[#1A1A18]/60 whitespace-nowrap">{{ fmtDate(don.donated_at) }}</td>
              <td class="px-4 py-3">
                <div class="flex gap-1">
                  <button v-if="don.status === 'pending'" class="p-1.5 rounded bg-green-500 text-white hover:brightness-110" @click="confirmDonation(don)" title="Confirmer">
                    <Check class="w-3.5 h-3.5" />
                  </button>
                  <button v-if="!don.receipt_sent && don.status === 'confirmed'" class="p-1.5 rounded border border-[#1A1A18]/12 text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="markReceipt(don)" title="Marquer reçu envoyé">
                    <Mail class="w-3.5 h-3.5" />
                  </button>
                  <button class="p-1.5 rounded border border-[#1A1A18]/12 text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="viewDonation(don)" title="Détails">
                    <Eye class="w-3.5 h-3.5" />
                  </button>
                  <button class="p-1.5 rounded bg-red-500 text-white hover:brightness-110" @click="deleteDonation(don)" title="Supprimer">
                    <Trash2 class="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.last_page > 1" class="flex justify-center gap-2 mt-2">
        <button class="w-9 h-9 rounded border border-[#1A1A18]/12 bg-white flex items-center justify-center text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A] disabled:opacity-35 disabled:cursor-not-allowed" :disabled="pagination.current_page === 1" @click="changePage(pagination.current_page - 1)">
          <ChevronLeft class="w-4 h-4" />
        </button>
        <button v-for="p in displayedPages" :key="p" class="w-9 h-9 rounded border border-[#1A1A18]/12 bg-white flex items-center justify-center text-sm hover:border-[#C85A2A] hover:text-[#C85A2A]" :class="{'bg-[#1B3A2D] text-white border-[#1B3A2D] hover:bg-[#1B3A2D]': pagination.current_page === p}" @click="changePage(p)">
          {{ p }}
        </button>
        <button class="w-9 h-9 rounded border border-[#1A1A18]/12 bg-white flex items-center justify-center text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A] disabled:opacity-35 disabled:cursor-not-allowed" :disabled="pagination.current_page === pagination.last_page" @click="changePage(pagination.current_page + 1)">
          <ChevronRight class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════
         TAB 2 — PARAMÈTRES
    ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'settings'" class="flex flex-col gap-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <!-- Card Activation -->
        <div class="bg-white rounded-lg p-6 shadow-sm" :class="{'border-l-4 border-l-[#C85A2A]': !donationsEnabled}">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 rounded-lg flex items-center justify-center" :style="{background: donationsEnabled ? 'rgba(122,158,135,.15)' : 'rgba(200,90,42,.1)'}">
              <component :is="donationsEnabled ? ToggleRight : ToggleLeft" :style="{color: donationsEnabled ? '#7A9E87' : '#C85A2A'}" class="w-6 h-6" />
            </div>
            <div>
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">Collecte de dons</h3>
              <p class="text-sm font-medium mt-1" :class="donationsEnabled ? 'text-green-600' : 'text-red-600'">
                {{ donationsEnabled ? '✅ Activée' : '❌ Désactivée' }}
              </p>
            </div>
          </div>
          <p class="text-sm text-[#1A1A18]/60 leading-relaxed mb-4">
            {{ donationsEnabled ? 'Les visiteurs peuvent actuellement effectuer des dons sur le site public.' : 'La collecte est suspendue. Aucun don ne peut être effectué par les visiteurs.' }}
          </p>
          <button class="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition" :class="donationsEnabled ? 'bg-red-500 text-white hover:brightness-110' : 'bg-green-500 text-white hover:brightness-110'" @click="openToggleModal">
            <component :is="donationsEnabled ? ToggleLeft : ToggleRight" class="w-4 h-4" />
            {{ donationsEnabled ? 'Désactiver la collecte' : 'Activer la collecte' }}
          </button>
        </div>

        <!-- Message désactivé -->
        <div class="bg-white rounded-lg p-6 shadow-sm">
          <h3 class="font-serif text-lg font-semibold text-[#1A1A18] mb-2">Message si désactivé</h3>
          <p class="text-sm text-[#1A1A18]/60 mb-4">Affiché aux visiteurs quand les dons sont désactivés.</p>
          <textarea v-model="settings.disabledMessage" rows="3" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A] resize-vertical"></textarea>
          <button class="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A] transition" @click="saveTextSetting('donations_disabled_message', settings.disabledMessage)">
            <Save class="w-4 h-4" />Enregistrer
          </button>
        </div>

        <!-- Montant minimum -->
        <div class="bg-white rounded-lg p-6 shadow-sm">
          <h3 class="font-serif text-lg font-semibold text-[#1A1A18] mb-2">Montant minimum (EUR)</h3>
          <p class="text-sm text-[#1A1A18]/60 mb-4">Les autres devises sont converties pour vérification.</p>
          <input v-model.number="settings.minAmount" type="number" min="1" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
          <button class="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A] transition" @click="saveTextSetting('min_donation_amount_eur', settings.minAmount)">
            <Save class="w-4 h-4" />Enregistrer
          </button>
        </div>

        <!-- Méthodes de paiement -->
        <div class="bg-white rounded-lg p-6 shadow-sm">
          <h3 class="font-serif text-lg font-semibold text-[#1A1A18] mb-4">Méthodes de paiement</h3>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-[#1A1A18]">Stripe (carte bancaire)</span>
              <div class="w-11 h-6 rounded-full bg-[#1A1A18]/12 relative transition-colors cursor-pointer" :class="{'bg-[#C85A2A]': settings.stripeEnabled}" @click="togglePayMethod('stripe_enabled', settings.stripeEnabled)">
                <span class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform" :class="{'translate-x-5': settings.stripeEnabled}"></span>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-[#1A1A18]">Mobile Money</span>
              <div class="w-11 h-6 rounded-full bg-[#1A1A18]/12 relative transition-colors cursor-pointer" :class="{'bg-[#C85A2A]': settings.mobileEnabled}" @click="togglePayMethod('mobile_money_enabled', settings.mobileEnabled)">
                <span class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform" :class="{'translate-x-5': settings.mobileEnabled}"></span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ════════════════════════════════════════════════════
         TAB 3 — MONTANTS PAR DÉFAUT
    ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'amounts'" class="flex flex-col gap-6">
      <!-- Sélecteur de devise -->
      <div class="flex flex-wrap items-center gap-3 bg-white rounded-lg p-5 shadow-sm">
        <span class="text-sm font-medium text-[#1A1A18]/60">Filtrer par devise :</span>
        <button
          v-for="c in currencies"
          :key="c.code"
          class="inline-flex items-center px-3 py-1 rounded-full bg-[#EDE4CC] text-[#1A1A18]/60 text-xs transition-all"
          :class="{'bg-[#C85A2A] text-white': amountsFilter === c.code}"
          @click="amountsFilter = c.code; loadDefaultAmounts()"
        >
          {{ c.code }} {{ c.symbol }}
        </button>
        <button class="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#C85A2A] text-white rounded-md text-sm hover:brightness-110 transition" @click="openAmountModal(null)">
          <Plus class="w-4 h-4" />Ajouter un montant
        </button>
      </div>

      <!-- Table montants -->
      <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="w-9 h-9 rounded-full border-3 border-[#1A1A18]/12 border-t-[#C85A2A] animate-spin"></div>
        </div>
        <table v-else class="w-full text-sm">
          <thead class="bg-[#F5EFE0]">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Devise</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Montant</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Label (impact)</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Ordre</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Actif</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-[#1A1A18]/60 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!defaultAmounts.length">
              <td colspan="6" class="text-center text-[#1A1A18]/60 py-8">Aucun montant configuré</td>
            </tr>
            <tr v-for="amt in defaultAmounts" :key="amt.id" class="border-b border-[#EDE4CC] hover:bg-[#F5EFE0] transition">
              <td class="px-4 py-3"><span class="inline-flex items-center px-2 py-1 rounded-full bg-[#EDE4CC] text-xs">{{ amt.currency_code }} {{ amt.currency?.symbol }}</span></td>
              <td class="px-4 py-3 font-semibold">{{ amt.amount.toLocaleString('fr-FR') }}</td>
              <td class="px-4 py-3">{{ amt.label || '—' }}</td>
              <td class="px-4 py-3">{{ amt.display_order }}</td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase" :class="amt.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'">
                  {{ amt.is_active ? 'Oui' : 'Non' }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="flex gap-1">
                  <button class="p-1.5 rounded border border-[#1A1A18]/12 text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="openAmountModal(amt)">
                    <Pencil class="w-3.5 h-3.5" />
                  </button>
                  <button class="p-1.5 rounded bg-red-500 text-white hover:brightness-110" @click="deleteDefaultAmount(amt)">
                    <Trash2 class="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════
         TAB 4 — DEVISES
    ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'currencies'" class="flex flex-col gap-6">
      <div class="flex justify-end">
        <button class="inline-flex items-center gap-2 px-4 py-2 bg-[#C85A2A] text-white rounded-md text-sm hover:brightness-110 transition" @click="openCurrencyModal(null)">
          <Plus class="w-4 h-4" />Ajouter une devise
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="cur in currencies" :key="cur.code" class="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition" :class="{'opacity-65': !cur.is_active}">
          <div class="flex items-center gap-3 p-5 border-b border-[#1A1A18]/12 flex-wrap">
            <div class="font-serif text-3xl font-bold text-[#C85A2A] w-12 text-center">{{ cur.symbol }}</div>
            <div>
              <p class="font-semibold text-[#1A1A18] flex items-center gap-1 flex-wrap">{{ cur.code }} <span v-if="cur.is_base" class="bg-[#1B3A2D] text-white text-xs px-2 py-0.5 rounded">Base</span></p>
              <p class="text-xs text-[#1A1A18]/60">{{ cur.name }}</p>
            </div>
            <div class="flex gap-1 ml-auto">
              <button class="p-1.5 rounded border border-[#1A1A18]/12 text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="openRateModal(cur)" title="Modifier le taux">
                <RefreshCw class="w-3.5 h-3.5" />
              </button>
              <button v-if="!cur.is_base" class="p-1.5 rounded border border-[#1A1A18]/12 text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="toggleCurrency(cur)" :title="cur.is_active ? 'Désactiver' : 'Activer'">
                <component :is="cur.is_active ? ToggleRight : ToggleLeft" class="w-3.5 h-3.5" />
              </button>
              <button v-if="!cur.is_base" class="p-1.5 rounded bg-red-500 text-white hover:brightness-110" @click="deleteCurrency(cur)">
                <Trash2 class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div class="p-5 space-y-2">
            <div class="flex justify-between text-sm"><span class="text-[#1A1A18]/60">Taux (1 {{ cur.code }} = X EUR)</span><strong class="text-[#1A1A18]">{{ cur.rate_to_eur }}</strong></div>
            <div class="flex justify-between text-sm"><span class="text-[#1A1A18]/60">1 EUR =</span><strong class="text-[#1A1A18]">{{ cur.rate_to_eur > 0 ? Math.round(1/cur.rate_to_eur).toLocaleString('fr-FR') : '—' }} {{ cur.code }}</strong></div>
            <div class="flex justify-between text-sm"><span class="text-[#1A1A18]/60">Montants configurés</span><strong class="text-[#1A1A18]">{{ cur.amounts_count || 0 }}</strong></div>
          </div>
          <div class="py-2 text-center text-xs font-semibold uppercase" :class="cur.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'">
            {{ cur.is_active ? 'Active' : 'Inactive' }}
          </div>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════
         MODALES
    ════════════════════════════════════════════════════ -->

    <!-- Modal Toggle Dons -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showToggleModal" class="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" @click.self="showToggleModal = false">
          <div class="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div class="flex items-center justify-between p-5 border-b border-[#1A1A18]/12 sticky top-0 bg-white">
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">{{ donationsEnabled ? 'Désactiver la collecte' : 'Activer la collecte' }}</h3>
              <button class="w-8 h-8 rounded bg-[#F5EFE0] flex items-center justify-center text-[#1A1A18]/60 hover:bg-[#EDE4CC]" @click="showToggleModal = false">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div class="flex gap-3 p-3 rounded-lg" :class="donationsEnabled ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'">
                <component :is="donationsEnabled ? AlertTriangle : CheckCircle" class="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p class="text-sm">{{ donationsEnabled ? 'Vous êtes sur le point de suspendre la collecte de dons. Aucun don ne pourra être effectué.' : 'Vous allez réactiver la collecte de dons sur le site public.' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Confirmez votre mot de passe administrateur <span class="text-[#C85A2A]">*</span></label>
                <div class="relative">
                  <input v-model="togglePassword" :type="showPwd ? 'text' : 'password'" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A] pr-10" placeholder="Votre mot de passe" @keyup.enter="submitToggle" />
                  <button class="absolute right-2 top-1/2 -translate-y-1/2 text-[#1A1A18]/60 hover:text-[#C85A2A]" @click="showPwd = !showPwd" type="button">
                    <Eye class="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p v-if="toggleError" class="text-sm text-red-600">{{ toggleError }}</p>
            </div>
            <div class="flex justify-end gap-3 p-5 border-t border-[#1A1A18]/12 sticky bottom-0 bg-white">
              <button class="px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="showToggleModal = false">Annuler</button>
              <button class="px-4 py-2 rounded-md text-sm font-medium text-white transition" :class="donationsEnabled ? 'bg-red-500 hover:brightness-110' : 'bg-green-500 hover:brightness-110'" :disabled="!togglePassword || toggleLoading" @click="submitToggle">
                <span v-if="toggleLoading" class="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                {{ donationsEnabled ? 'Désactiver' : 'Activer' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Taux Devise -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showRateModal" class="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" @click.self="showRateModal = false">
          <div class="bg-white rounded-xl max-w-md w-full">
            <div class="flex items-center justify-between p-5 border-b border-[#1A1A18]/12">
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">Modifier le taux — {{ rateForm.code }}</h3>
              <button class="w-8 h-8 rounded bg-[#F5EFE0] flex items-center justify-center text-[#1A1A18]/60 hover:bg-[#EDE4CC]" @click="showRateModal = false">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Taux (1 {{ rateForm.code }} = X EUR) <span class="text-[#C85A2A]">*</span></label>
                <input v-model.number="rateForm.rate" type="number" step="0.000001" min="0.000001" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
                <p v-if="rateForm.rate > 0" class="text-xs text-[#7A9E87] mt-1">1 EUR = {{ Math.round(1/rateForm.rate).toLocaleString('fr-FR') }} {{ rateForm.code }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Mot de passe administrateur <span class="text-[#C85A2A]">*</span></label>
                <div class="relative">
                  <input v-model="rateForm.password" :type="showPwd ? 'text' : 'password'" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A] pr-10" placeholder="Votre mot de passe" />
                  <button class="absolute right-2 top-1/2 -translate-y-1/2 text-[#1A1A18]/60 hover:text-[#C85A2A]" @click="showPwd = !showPwd" type="button">
                    <Eye class="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p v-if="rateError" class="text-sm text-red-600">{{ rateError }}</p>
            </div>
            <div class="flex justify-end gap-3 p-5 border-t border-[#1A1A18]/12">
              <button class="px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="showRateModal = false">Annuler</button>
              <button class="px-4 py-2 bg-[#C85A2A] text-white rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50" :disabled="!rateForm.password || !rateForm.rate || toggleLoading" @click="submitRateUpdate">
                <span v-if="toggleLoading" class="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Devise -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCurrencyModal" class="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" @click.self="showCurrencyModal = false">
          <div class="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div class="flex items-center justify-between p-5 border-b border-[#1A1A18]/12 sticky top-0 bg-white">
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">{{ currencyForm.id ? 'Modifier la devise' : 'Nouvelle devise' }}</h3>
              <button class="w-8 h-8 rounded bg-[#F5EFE0] flex items-center justify-center text-[#1A1A18]/60 hover:bg-[#EDE4CC]" @click="showCurrencyModal = false">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div v-if="!currencyForm.id">
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Code ISO <span class="text-[#C85A2A]">*</span></label>
                <input v-model="currencyForm.code" type="text" maxlength="10" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" placeholder="XAF, GBP…" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Nom <span class="text-[#C85A2A]">*</span></label>
                <input v-model="currencyForm.name" type="text" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" placeholder="Franc CFA" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Symbole <span class="text-[#C85A2A]">*</span></label>
                <input v-model="currencyForm.symbol" type="text" maxlength="10" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" placeholder="FCFA" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Taux (1 {{ currencyForm.code||'?' }} = X EUR) <span class="text-[#C85A2A]">*</span></label>
                <input v-model.number="currencyForm.rate_to_eur" type="number" step="0.000001" min="0.000001" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
                <p v-if="currencyForm.rate_to_eur > 0" class="text-xs text-[#7A9E87] mt-1">1 EUR ≈ {{ Math.round(1/currencyForm.rate_to_eur).toLocaleString('fr-FR') }} {{ currencyForm.code }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Ordre d'affichage</label>
                <input v-model.number="currencyForm.display_order" type="number" min="0" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
              </div>
              <p v-if="currencyError" class="text-sm text-red-600">{{ currencyError }}</p>
            </div>
            <div class="flex justify-end gap-3 p-5 border-t border-[#1A1A18]/12 sticky bottom-0 bg-white">
              <button class="px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="showCurrencyModal = false">Annuler</button>
              <button class="px-4 py-2 bg-[#C85A2A] text-white rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50" :disabled="toggleLoading" @click="submitCurrency">
                <span v-if="toggleLoading" class="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Montant -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showAmountModal" class="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" @click.self="showAmountModal = false">
          <div class="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div class="flex items-center justify-between p-5 border-b border-[#1A1A18]/12 sticky top-0 bg-white">
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">{{ amountForm.id ? 'Modifier le montant' : 'Nouveau montant par défaut' }}</h3>
              <button class="w-8 h-8 rounded bg-[#F5EFE0] flex items-center justify-center text-[#1A1A18]/60 hover:bg-[#EDE4CC]" @click="showAmountModal = false">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Devise <span class="text-[#C85A2A]">*</span></label>
                <select v-model="amountForm.currency_code" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]">
                  <option v-for="c in currencies" :key="c.code" :value="c.code">{{ c.code }} — {{ c.name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Montant <span class="text-[#C85A2A]">*</span></label>
                <input v-model.number="amountForm.amount" type="number" min="0.01" step="0.01" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Label / Description de l'impact</label>
                <input v-model="amountForm.label" type="text" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" placeholder="ex: 1 semaine de formation" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Ordre</label>
                <input v-model.number="amountForm.display_order" type="number" min="0" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-[#1A1A18] mb-1">Actif</label>
                <select v-model="amountForm.is_active" class="w-full px-3 py-2 border border-[#1A1A18]/12 rounded-md text-sm focus:outline-none focus:border-[#C85A2A]">
                  <option :value="true">Oui</option>
                  <option :value="false">Non</option>
                </select>
              </div>
              <p v-if="amountError" class="text-sm text-red-600">{{ amountError }}</p>
            </div>
            <div class="flex justify-end gap-3 p-5 border-t border-[#1A1A18]/12 sticky bottom-0 bg-white">
              <button class="px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="showAmountModal = false">Annuler</button>
              <button class="px-4 py-2 bg-[#C85A2A] text-white rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50" :disabled="toggleLoading" @click="submitAmount">
                <span v-if="toggleLoading" class="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Détail Don -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showDonModal && selectedDon" class="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" @click.self="showDonModal = false">
          <div class="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="flex items-center justify-between p-5 border-b border-[#1A1A18]/12 sticky top-0 bg-white">
              <h3 class="font-serif text-lg font-semibold text-[#1A1A18]">Don #{{ selectedDon.invoice_number || selectedDon.id }}</h3>
              <button class="w-8 h-8 rounded bg-[#F5EFE0] flex items-center justify-center text-[#1A1A18]/60 hover:bg-[#EDE4CC]" @click="showDonModal = false">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="p-5">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Donateur</div><div class="font-medium">{{ selectedDon.is_anonymous ? 'Anonyme' : selectedDon.donor_name }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Email</div><div class="font-medium">{{ selectedDon.donor_email || '—' }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Téléphone</div><div class="font-medium">{{ selectedDon.donor_phone || '—' }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Montant</div><div class="font-medium">{{ selectedDon.amount }} {{ selectedDon.currency_code }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Équivalent EUR</div><div class="font-medium">{{ fmtEur(selectedDon.amount_eur) }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Taux utilisé</div><div class="font-medium">{{ selectedDon.exchange_rate }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Projet</div><div class="font-medium">{{ selectedDon.project?.title || selectedDon.project_slug || '—' }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Méthode</div><div class="font-medium">{{ selectedDon.payment_method }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Transaction</div><div class="font-medium">{{ selectedDon.transaction_id || '—' }}</div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Statut</div><div><span class="inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase" :class="{'bg-amber-100 text-amber-700': selectedDon.status === 'pending', 'bg-green-100 text-green-700': selectedDon.status === 'confirmed', 'bg-red-100 text-red-700': selectedDon.status === 'failed'}">{{ statusLabel(selectedDon.status) }}</span></div></div>
                <div class="bg-[#F5EFE0] rounded p-3"><div class="text-xs text-[#1A1A18]/60">Reçu envoyé</div><div class="font-medium">{{ selectedDon.receipt_sent ? 'Oui' : 'Non' }}</div></div>
                <div v-if="selectedDon.message" class="bg-[#F5EFE0] rounded p-3 md:col-span-2"><div class="text-xs text-[#1A1A18]/60">Message</div><div class="font-medium">{{ selectedDon.message }}</div></div>
              </div>
            </div>
            <div class="flex justify-end gap-3 p-5 border-t border-[#1A1A18]/12 sticky bottom-0 bg-white">
              <button class="px-4 py-2 border border-[#1A1A18]/12 rounded-md text-sm text-[#1A1A18]/60 hover:border-[#C85A2A] hover:text-[#C85A2A]" @click="showDonModal = false">Fermer</button>
              <button v-if="selectedDon.status === 'pending'" class="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:brightness-110" @click="confirmDonation(selectedDon); showDonModal=false">
                <Check class="w-4 h-4 inline mr-1" />Confirmer le don
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Toast -->
    <Transition name="toast">
      <div v-if="toast.msg" class="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg text-white shadow-lg" :class="toast.type === 'error' ? 'bg-red-700' : 'bg-[#1B3A2D]'">
        <component :is="toast.type === 'error' ? XCircle : CheckCircle" class="w-5 h-5" />
        {{ toast.msg }}
      </div>
    </Transition>

  </main>
</template>

<script>
import { debounce } from 'lodash'
import axios from 'axios'
import {
  Heart, Settings, DollarSign, Grid3x3,
  Search, Download, Check, Mail, Eye, Trash2, Plus, Pencil, Save,
  RefreshCw, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle,
  X, XCircle, ChevronLeft, ChevronRight,
} from 'lucide-vue-next'

const API_BASE = () => {
  const token = localStorage.getItem('token')
  return axios.create({
    baseURL: 'https://api.stagfondation.org/api/admin',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  })
}

export default {
  name: 'AdminDonations',
  components: {
    Heart, Settings, DollarSign, Grid3x3,
    Search, Download, Check, Mail, Eye, Trash2, Plus, Pencil, Save,
    RefreshCw, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle,
    X, XCircle, ChevronLeft, ChevronRight,
  },

  data() {
    return {
      activeTab: 'settings',
      loading: false,

      tabs: [
        { id:'donations',  label:'Dons',           icon:'Heart',      badge: null },
        { id:'settings',   label:'Paramètres',      icon:'Settings',   badge: null },
        { id:'amounts',    label:'Montants',         icon:'DollarSign', badge: null },
        { id:'currencies', label:'Devises',          icon:'Grid3x3',    badge: null },
      ],

      donations: [],
      pagination: { current_page:1, last_page:1, total:0 },
      displayedPages: [],
      donationStats: {},
      filters: { search:'', status:'', currency_code:'', payment_method:'' },
      selectedDon: null,
      showDonModal: false,

      donationsEnabled: true,
      settings: { disabledMessage:'', minAmount:5, stripeEnabled:true, mobileEnabled:true },
      showToggleModal: false,
      togglePassword: '',
      toggleError: '',
      toggleLoading: false,
      showPwd: false,

      showRateModal: false,
      rateForm: { id:null, code:'', rate:null, password:'' },
      rateError: '',

      currencies: [],
      showCurrencyModal: false,
      currencyForm: { id:null, code:'', name:'', symbol:'', rate_to_eur:null, display_order:0 },
      currencyError: '',

      defaultAmounts: [],
      amountsFilter: '',
      showAmountModal: false,
      amountForm: { id:null, currency_code:'EUR', amount:null, label:'', is_active:true, display_order:0 },
      amountError: '',

      toast: { msg:'', type:'success' },
    }
  },

  computed: {
    donationKpis() {
      return [
        { label:'Total collecté (EUR)',    value: `${(this.donationStats.total_collected||0).toFixed(2)} €` },
        { label:'Nombre de dons',          value: this.donationStats.total_count || 0 },
        { label:'Dons en attente',         value: this.donationStats.pending_count || 0 },
        { label:'Ce mois (EUR)',           value: `${(this.donationStats.this_month||0).toFixed(2)} €` },
        { label:'Don moyen (EUR)',         value: `${(this.donationStats.average_donation||0).toFixed(2)} €` },
      ]
    }
  },

  watch: {
    activeTab(tab) {
      if (tab === 'donations')  this.loadDonations()
      if (tab === 'settings')   this.loadSettings()
      if (tab === 'currencies') this.loadCurrencies()
      if (tab === 'amounts')    this.loadDefaultAmounts()
    }
  },

  created() {
    this.debounceSearch = debounce(() => this.loadDonations(), 500)
    this.loadDonations()
    this.loadCurrencies()
    this.loadSettings()
  },

  methods: {
    api() { return API_BASE() },

    showToast(msg, type='success') {
      this.toast = { msg, type }
      setTimeout(() => { this.toast = { msg:'', type:'success' } }, 4000)
    },

    async loadDonations() {
      this.loading = true
      try {
        const [dRes, sRes] = await Promise.all([
          this.api().get('/donations', { params: { ...this.filters, per_page:15, page: this.pagination.current_page } }),
          this.api().get('/donations/stats'),
        ])
        this.donations = dRes.data.data
        this.pagination = { current_page: dRes.data.current_page, last_page: dRes.data.last_page, total: dRes.data.total }
        this.donationStats = sRes.data.data
        this.updateDisplayedPages()
      } catch (e) { this.showToast('Erreur de chargement', 'error') }
      finally { this.loading = false }
    },

    async confirmDonation(don) {
      try {
        await this.api().patch(`/donations/${don.id}/confirm`)
        this.showToast('Don confirmé !')
        this.loadDonations()
      } catch { this.showToast('Erreur lors de la confirmation', 'error') }
    },

    async markReceipt(don) {
      try {
        await this.api().patch(`/donations/${don.id}/receipt`)
        this.showToast('Reçu marqué comme envoyé')
        this.loadDonations()
      } catch { this.showToast('Erreur', 'error') }
    },

    async deleteDonation(don) {
      if (!confirm(`Supprimer le don #${don.invoice_number || don.id} ?`)) return
      try {
        await this.api().delete(`/donations/${don.id}`)
        this.showToast('Don supprimé')
        this.loadDonations()
      } catch { this.showToast('Erreur lors de la suppression', 'error') }
    },

    viewDonation(don) { this.selectedDon = don; this.showDonModal = true },
    exportCsv() { window.open('data:text/plain,CSV export à implémenter') },

    changePage(p) {
      if (p >= 1 && p <= this.pagination.last_page) {
        this.pagination.current_page = p
        this.loadDonations()
      }
    },

    updateDisplayedPages() {
      const c = this.pagination.current_page, l = this.pagination.last_page, d = 2
      const pages = []
      for (let i = 1; i <= l; i++) {
        if (i === 1 || i === l || (i >= c-d && i <= c+d)) pages.push(i)
      }
      this.displayedPages = pages
    },

    async loadSettings() {
      try {
        const { data } = await this.api().get('/donation-settings')
        const s = data.data
        this.donationsEnabled = s.donations_enabled
        this.settings.disabledMessage = s.donations_disabled_message
        this.settings.minAmount = s.min_donation_amount_eur
        this.settings.stripeEnabled = s.stripe_enabled
        this.settings.mobileEnabled = s.mobile_money_enabled
      } catch {}
    },

    openToggleModal() { this.togglePassword = ''; this.toggleError = ''; this.showToggleModal = true },

    async submitToggle() {
      if (!this.togglePassword) { this.toggleError = 'Mot de passe requis.'; return }
      this.toggleLoading = true; this.toggleError = ''
      try {
        const { data } = await this.api().post('/donation-settings/toggle', {
          password: this.togglePassword,
          enabled: !this.donationsEnabled,
        })
        this.donationsEnabled = data.donations_enabled
        this.showToggleModal = false
        this.showToast(data.message)
      } catch (e) {
        this.toggleError = e.response?.data?.message || 'Mot de passe incorrect.'
      } finally { this.toggleLoading = false }
    },

    async saveTextSetting(key, value) {
      const pwd = prompt('Confirmez votre mot de passe administrateur :')
      if (!pwd) return
      try {
        await this.api().post('/donation-settings/update', { key, value, password: pwd })
        this.showToast('Paramètre mis à jour')
      } catch (e) {
        this.showToast(e.response?.data?.message || 'Erreur', 'error')
      }
    },

    async togglePayMethod(key, currentVal) {
      const pwd = prompt('Confirmez votre mot de passe administrateur :')
      if (!pwd) return
      try {
        await this.api().post('/donation-settings/update', { key, value: !currentVal ? 'true' : 'false', password: pwd })
        if (key === 'stripe_enabled') this.settings.stripeEnabled = !currentVal
        if (key === 'mobile_money_enabled') this.settings.mobileEnabled = !currentVal
        this.showToast('Paramètre mis à jour')
      } catch (e) { this.showToast(e.response?.data?.message || 'Erreur', 'error') }
    },

    async loadCurrencies() {
      try {
        const { data } = await this.api().get('/currencies')
        this.currencies = data.data
        this.tabs[3].badge = this.currencies.length
      } catch {}
    },

    openCurrencyModal(cur) {
      this.currencyError = ''
      this.currencyForm = cur
        ? { id: cur.id, code: cur.code, name: cur.name, symbol: cur.symbol, rate_to_eur: cur.rate_to_eur, display_order: cur.display_order }
        : { id:null, code:'', name:'', symbol:'', rate_to_eur:null, display_order: this.currencies.length + 1 }
      this.showCurrencyModal = true
    },

    async submitCurrency() {
      this.currencyError = ''
      if (!this.currencyForm.code || !this.currencyForm.name || !this.currencyForm.symbol || !this.currencyForm.rate_to_eur) {
        this.currencyError = 'Tous les champs obligatoires doivent être remplis.'; return
      }
      this.toggleLoading = true
      try {
        const url = this.currencyForm.id ? `/currencies/${this.currencyForm.id}` : '/currencies'
        await this.api().post(url, this.currencyForm)
        this.showCurrencyModal = false
        this.showToast(this.currencyForm.id ? 'Devise mise à jour' : 'Devise créée')
        this.loadCurrencies()
      } catch (e) { this.currencyError = e.response?.data?.message || 'Erreur' }
      finally { this.toggleLoading = false }
    },

    openRateModal(cur) {
      this.rateError = ''; this.showPwd = false
      this.rateForm = { id: cur.id, code: cur.code, rate: cur.rate_to_eur, password: '' }
      this.showRateModal = true
    },

    async submitRateUpdate() {
      this.rateError = ''; this.toggleLoading = true
      try {
        await this.api().patch(`/currencies/${this.rateForm.id}/rate`, { rate_to_eur: this.rateForm.rate, password: this.rateForm.password })
        this.showRateModal = false
        this.showToast('Taux mis à jour')
        this.loadCurrencies()
      } catch (e) { this.rateError = e.response?.data?.message || 'Mot de passe incorrect.' }
      finally { this.toggleLoading = false }
    },

    async toggleCurrency(cur) {
      try {
        await this.api().patch(`/currencies/${cur.id}/toggle`)
        this.showToast(cur.is_active ? 'Devise désactivée' : 'Devise activée')
        this.loadCurrencies()
      } catch { this.showToast('Erreur', 'error') }
    },

    async deleteCurrency(cur) {
      if (!confirm(`Supprimer la devise ${cur.code} ?`)) return
      try {
        await this.api().delete(`/currencies/${cur.id}`)
        this.showToast('Devise supprimée')
        this.loadCurrencies()
      } catch (e) { this.showToast(e.response?.data?.message || 'Erreur', 'error') }
    },

    async loadDefaultAmounts() {
      this.loading = true
      try {
        const { data } = await this.api().get('/default-amounts', {
          params: this.amountsFilter ? { currency_code: this.amountsFilter } : {}
        })
        this.defaultAmounts = data.data
      } catch { this.showToast('Erreur', 'error') }
      finally { this.loading = false }
    },

    openAmountModal(amt) {
      this.amountError = ''
      this.amountForm = amt
        ? { id: amt.id, currency_code: amt.currency_code, amount: amt.amount, label: amt.label, is_active: amt.is_active, display_order: amt.display_order }
        : { id:null, currency_code: this.amountsFilter || 'EUR', amount:null, label:'', is_active:true, display_order:0 }
      this.showAmountModal = true
    },

    async submitAmount() {
      this.amountError = ''
      if (!this.amountForm.currency_code || !this.amountForm.amount) { this.amountError = 'Devise et montant obligatoires.'; return }
      this.toggleLoading = true
      try {
        const url = this.amountForm.id ? `/default-amounts/${this.amountForm.id}` : '/default-amounts'
        await this.api().post(url, this.amountForm)
        this.showAmountModal = false
        this.showToast(this.amountForm.id ? 'Montant mis à jour' : 'Montant créé')
        this.loadDefaultAmounts()
      } catch (e) { this.amountError = e.response?.data?.message || 'Erreur' }
      finally { this.toggleLoading = false }
    },

    async deleteDefaultAmount(amt) {
      if (!confirm(`Supprimer ce montant (${amt.amount} ${amt.currency_code}) ?`)) return
      try {
        await this.api().delete(`/default-amounts/${amt.id}`)
        this.showToast('Montant supprimé')
        this.loadDefaultAmounts()
      } catch { this.showToast('Erreur', 'error') }
    },

    fmtEur(v) { return `${Number(v||0).toFixed(2)} €` },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric'}) : '—' },
    statusLabel(s) { return { pending:'En attente', confirmed:'Confirmé', failed:'Échoué', refunded:'Remboursé' }[s] || s },
  }
}
</script>

<style scoped>
/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateY(8px); }
</style>